/**
 * CoachMark (v8)
 *
 * Changes from v7:
 *   - Real keyboard detection via Capacitor's keyboardWillShow /
 *     keyboardWillHide window events, with fallback to visualViewport
 *     height comparison. Capacitor's KeyboardFix.swift in this app
 *     resizes the WebView itself, so visualViewport.height matches
 *     window.innerHeight even with the keyboard up — that's why my
 *     previous detection never fired. The Capacitor Keyboard plugin
 *     dispatches keyboardWillShow/keyboardWillHide events on `window`
 *     regardless of WebView resize behavior.
 *
 * Visual design unchanged from v7 (white card, theme-color step label,
 * plain DM Sans title and body).
 */
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { isPurpleOn } from '../../utils/purple';
import { isPlanOn } from '../../utils/plan'; // w2_07

// w2_07: Worthly 2.0 uses its own font for the tour bubbles.
const tourFont = () => (isPlanOn() ? "'DM Sans', -apple-system, 'Helvetica Neue', sans-serif" /* w2_11 */ : "'DM Sans', sans-serif");

const PADDING = 10;
const CORNER_RADIUS = 14;
const FALLBACK_MS = 2000;
const RECHECK_MS = 150;
const DIM_OPACITY = 0.35;
const KEYBOARD_THRESHOLD_PX = 100;

const getThemeTokens = (theme) => {
  if (theme === 'warm') {
    return { accent: '#5a9e78', accentDark: '#2D5A3D' };
  }
  if (theme === 'blue') {
    return { accent: '#6381f0', accentDark: '#4C3BCF' };
  }
  if (isPlanOn()) {
    return { accent: '#B9A6F0', accentDark: '#7A5DD3' }; // w2_07: lavender
  }
  if (isPurpleOn()) {
    return { accent: '#AC8DF2', accentDark: '#8B5CE0' };
  }
  return { accent: '#e879a0', accentDark: '#e879a0' };
};

const getEffectiveViewportHeight = () => {
  if (typeof window === 'undefined') return 812;
  if (window.visualViewport && window.visualViewport.height) {
    return window.visualViewport.height;
  }
  return window.innerHeight;
};

const detectKeyboardFromViewport = () => {
  if (typeof window === 'undefined' || !window.visualViewport) return false;
  return (window.innerHeight - window.visualViewport.height) > KEYBOARD_THRESHOLD_PX;
};

const computeTooltipPlacement = (rect, tooltipHeight, effectiveH) => {
  const spaceAbove = rect.top;
  const spaceBelow = effectiveH - rect.bottom;
  if (spaceAbove >= tooltipHeight + 32) return 'above';
  if (spaceBelow >= tooltipHeight + 32) return 'below';
  return spaceBelow >= spaceAbove ? 'below' : 'above';
};

const CoachMark = ({
  targetName,
  title,
  body,
  stepIndex,
  stepTotal,
  theme = 'pink',
  onFallback,
  onSkip,
  suppressed = false,
}) => {
  const [rect, setRect] = useState(null);
  const [placement, setPlacement] = useState('below');
  const [viewport, setViewport] = useState({
    w: typeof window !== 'undefined' ? window.innerWidth : 375,
    h: typeof window !== 'undefined' ? window.innerHeight : 812,
    effectiveH: getEffectiveViewportHeight(),
  });
  const [keyboardUp, setKeyboardUp] = useState(detectKeyboardFromViewport());
  // Pass 14: once the user has tapped the target element, suppress the
  // coach-mark for the rest of this CoachMark instance's life. This closes
  // the visible gap between tap-submit and completion-detected in the
  // TourController (data propagation takes ~200-800ms).
  const [userActed, setUserActed] = useState(false);
  const tooltipRef = useRef(null);
  const fallbackTimerRef = useRef(null);
  const fallbackFiredRef = useRef(false);
  const lastScrollRef = useRef(0);
  // Set when the target still can't be found after a few seconds, so the
  // fallback card (with Skip tour) can show.
  const [missingLong, setMissingLong] = useState(false);

  const tokens = getThemeTokens(theme);

  // Keyboard detection: prefer Capacitor's explicit events, fall back to
  // visualViewport height comparison. Capacitor's KeyboardFix.swift in
  // this app resizes the WebView, so visualViewport-only detection misses
  // the keyboard. The Capacitor Keyboard plugin's keyboardWillShow /
  // keyboardWillHide window events fire regardless.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onShow = () => setKeyboardUp(true);
    const onHide = () => setKeyboardUp(false);

    window.addEventListener('keyboardWillShow', onShow);
    window.addEventListener('keyboardDidShow', onShow);
    window.addEventListener('keyboardWillHide', onHide);
    window.addEventListener('keyboardDidHide', onHide);

    let vvCheck;
    if (window.visualViewport) {
      vvCheck = () => {
        const detected = detectKeyboardFromViewport();
        // Only set true via this fallback — Capacitor events handle hide.
        // Avoids fighting with the Capacitor signal during animation.
        if (detected) setKeyboardUp(true);
      };
      window.visualViewport.addEventListener('resize', vvCheck);
    }

    return () => {
      window.removeEventListener('keyboardWillShow', onShow);
      window.removeEventListener('keyboardDidShow', onShow);
      window.removeEventListener('keyboardWillHide', onHide);
      window.removeEventListener('keyboardDidHide', onHide);
      if (vvCheck && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', vvCheck);
      }
    };
  }, []);

  // Pass 14: listen for taps on the target. First tap = user-acted, hide
  // the coach-mark immediately. Covers the window between submit-tap and
  // completion-detect-in-TourController (data propagation gap).
  // We rebind the listener in a polling loop because the target may mount
  // asynchronously, may detach/reattach during tab switches, and there's
  // no ref we control.
  useEffect(() => {
    let bound = null;
    const handler = () => setUserActed(true);

    const bind = () => {
      const el = document.querySelector(`[data-tour-target="${targetName}"]`);
      if (!el || el === bound) return;
      if (bound) {
        bound.removeEventListener('click', handler, true);
        bound.removeEventListener('touchend', handler, true);
      }
      el.addEventListener('click', handler, true);
      el.addEventListener('touchend', handler, true);
      bound = el;
    };

    bind();
    const poll = setInterval(bind, 300);
    return () => {
      clearInterval(poll);
      if (bound) {
        bound.removeEventListener('click', handler, true);
        bound.removeEventListener('touchend', handler, true);
      }
    };
  }, [targetName]);

  // If the user tapped the target but then backed out (e.g. closed the add
  // popup without saving), bring the coach-mark back. The target counts as
  // "backed out of" once it is on screen and nothing is covering it.
  useEffect(() => {
    if (!userActed) return;
    const actedAt = Date.now();
    const check = setInterval(() => {
      if (Date.now() - actedAt < 800) return;
      const el = document.querySelector(`[data-tour-target="${targetName}"]`);
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return;
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      if (cx < 0 || cy < 0 || cx > window.innerWidth || cy > window.innerHeight) return;
      const topEl = document.elementFromPoint(cx, cy);
      if (topEl && (topEl === el || el.contains(topEl))) setUserActed(false);
    }, 400);
    return () => clearInterval(check);
  }, [userActed, targetName]);

  // Don't rely only on iOS's "keyboard closed" event: if nothing is being
  // typed into and the viewport says there's no keyboard, show the bubble.
  useEffect(() => {
    if (!keyboardUp) return;
    const t = setInterval(() => {
      const a = document.activeElement;
      const typing = !!a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.isContentEditable);
      if (!typing && !detectKeyboardFromViewport()) setKeyboardUp(false);
    }, 500);
    return () => clearInterval(t);
  }, [keyboardUp]);

  useEffect(() => {
    if (rect) {
      setMissingLong(false);
      return;
    }
    const t = setTimeout(() => setMissingLong(true), FALLBACK_MS + 1000);
    return () => clearTimeout(t);
  }, [rect]);

  useLayoutEffect(() => {
    let cancelled = false;

    const measureOld = () => {
      if (cancelled) return false;
      const el = document.querySelector(`[data-tour-target="${targetName}"]`);
      return !!el;
    };

    // Keeps checking for the WHOLE step (not just the first 2 seconds), so a
    // slow-loading screen can't leave the tour running with no bubble.
    const remeasure = () => {
      if (cancelled) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const eh = getEffectiveViewportHeight();
      setViewport((prev) => (
        prev.w === vw && prev.h === vh && prev.effectiveH === eh ? prev : { w: vw, h: vh, effectiveH: eh }
      ));

      const el = document.querySelector(`[data-tour-target="${targetName}"]`);
      const r = el ? el.getBoundingClientRect() : null;
      if (!r || (r.width === 0 && r.height === 0)) {
        setRect(null);
        return;
      }

      // Scroll the target into view the first time, and again if it ends up
      // off screen (taps outside the target are blocked, so the user can't
      // scroll to it themselves).
      const offScreen = r.bottom < 0 || r.top > eh;
      // w2_07: only scroll when the target isn't already in full view, and
      // only as far as needed, so the page doesn't jump (Wishlist step).
      const inView = r.top >= 70 && r.bottom <= eh - 110;
      const now = Date.now();
      if (((lastScrollRef.current === 0 && !inView) || offScreen) && now - lastScrollRef.current > 1200) {
        lastScrollRef.current = now;
        try {
          el.style.scrollMarginTop = '96px';
          el.style.scrollMarginBottom = '120px';
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        } catch (e) {}
      } else if (lastScrollRef.current === 0) {
        lastScrollRef.current = now;
      }

      setRect((prev) => (
        prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height
          ? prev
          : { top: r.top, left: r.left, width: r.width, height: r.height }
      ));
    };

    remeasure();
    const poll = setInterval(remeasure, RECHECK_MS);
    fallbackTimerRef.current = setTimeout(() => {
      if (!measureOld() && !fallbackFiredRef.current && onFallback) {
        fallbackFiredRef.current = true;
        onFallback();
      }
    }, FALLBACK_MS);

    window.addEventListener('scroll', remeasure, true);
    window.addEventListener('resize', remeasure);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', remeasure);
      window.visualViewport.addEventListener('scroll', remeasure);
    }
    return () => {
      cancelled = true;
      clearInterval(poll);
      window.removeEventListener('scroll', remeasure, true);
      window.removeEventListener('resize', remeasure);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', remeasure);
        window.visualViewport.removeEventListener('scroll', remeasure);
      }
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    };
  }, [targetName, onFallback]);

  useLayoutEffect(() => {
    if (!rect) return;
    const h = tooltipRef.current?.offsetHeight || 140;
    setPlacement(computeTooltipPlacement(rect, h, viewport.effectiveH));
  }, [rect, viewport.effectiveH, title, body]);

  // Hide entire coach-mark when keyboard is up — user is typing, doesn't
  // need the tour chrome in the way. Reappears on keyboard dismiss.
  if (keyboardUp) return null;
  // Pass 14: once user tapped the target, hide immediately.
  if (userActed) return null;
  if (suppressed) return null;

  const skipLink = onSkip ? (
    <button
      type="button"
      onClick={onSkip}
      style={{
        marginTop: 10,
        padding: 0,
        background: 'none',
        border: 'none',
        fontSize: 12,
        fontWeight: 600,
        color: '#9ca3af',
        fontFamily: tourFont(),
        cursor: 'pointer',
      }}
      data-testid="coach-mark-skip"
    >
      Skip tour
    </button>
  ) : null;

  if (!rect) {
    // Target isn't on screen (still loading, or missing). Block the app so
    // the tour can't be wandered around with everything unlocked, and after
    // a moment offer a way out.
    return (
      <div
        className="fixed inset-0 flex items-center justify-center"
        style={{
          zIndex: 9998,
          pointerEvents: 'auto',
          background: missingLong ? `rgba(0,0,0,${DIM_OPACITY})` : 'transparent',
          padding: 16,
        }}
        data-testid="coach-mark-wait"
      >
        {missingLong && (
          <div
            className="rounded-2xl"
            style={{
              width: Math.min(320, viewport.w - 32),
              padding: '16px 18px',
              background: '#ffffff',
              boxShadow: '0 14px 44px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.05)',
            }}
          >
            <div style={{ fontFamily: tourFont(), fontWeight: 600, fontSize: 17, lineHeight: 1.25, color: '#1a1a1a', marginBottom: body ? 4 : 0 }}>
              {title}
            </div>
            {body && (
              <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.45, fontFamily: tourFont() }}>
                {body}
              </div>
            )}
            {skipLink}
          </div>
        )}
      </div>
    );
  }

  const holeX = Math.max(rect.left - PADDING, 0);
  const holeY = Math.max(rect.top - PADDING, 0);
  const holeW = rect.width + PADDING * 2;
  const holeH = rect.height + PADDING * 2;

  const tooltipMaxWidth = Math.min(320, viewport.w - 32);
  const tooltipGap = 18;
  const tooltipStyle = {};
  if (placement === 'below') {
    tooltipStyle.top = holeY + holeH + tooltipGap;
  } else {
    tooltipStyle.bottom = viewport.effectiveH - holeY + tooltipGap;
  }

  const tooltipIdealLeft = holeX + holeW / 2 - tooltipMaxWidth / 2;
  const tooltipLeft = Math.max(16, Math.min(viewport.w - tooltipMaxWidth - 16, tooltipIdealLeft));

  const hasStepLabel = typeof stepIndex === 'number' && typeof stepTotal === 'number';

  // Four invisible panels around the highlighted spot swallow taps, so only
  // the target (and the bubble) can be used during a step.
  const blockStyle = { position: 'fixed', zIndex: 9998, pointerEvents: 'auto', background: 'transparent' };

  return (
    <>
      <div style={{ ...blockStyle, left: 0, top: 0, width: '100%', height: holeY }} data-testid="coach-mark-block" />
      <div style={{ ...blockStyle, left: 0, top: holeY + holeH, width: '100%', bottom: 0 }} data-testid="coach-mark-block" />
      <div style={{ ...blockStyle, left: 0, top: holeY, width: holeX, height: holeH }} data-testid="coach-mark-block" />
      <div style={{ ...blockStyle, left: holeX + holeW, top: holeY, right: 0, height: holeH }} data-testid="coach-mark-block" />

      <div className="fixed inset-0" style={{ zIndex: 9998, pointerEvents: 'none' }} data-testid="coach-mark-dim">
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${viewport.w} ${viewport.h}`}
          preserveAspectRatio="none"
          style={{ position: 'absolute', inset: 0 }}
        >
          <defs>
            <mask id="coachMarkMask">
              <rect x={0} y={0} width={viewport.w} height={viewport.h} fill="white" />
              <rect x={holeX} y={holeY} width={holeW} height={holeH} rx={CORNER_RADIUS} ry={CORNER_RADIUS} fill="black" />
            </mask>
          </defs>
          <rect x={0} y={0} width={viewport.w} height={viewport.h} fill={`rgba(0,0,0,${DIM_OPACITY})`} mask="url(#coachMarkMask)" />
          <rect
            x={holeX - 1}
            y={holeY - 1}
            width={holeW + 2}
            height={holeH + 2}
            rx={CORNER_RADIUS + 1}
            ry={CORNER_RADIUS + 1}
            fill="none"
            stroke={tokens.accent}
            strokeWidth={2.5}
            opacity={0.95}
          >
            <animate attributeName="opacity" values="0.95;0.4;0.95" dur="1.6s" repeatCount="indefinite" />
          </rect>
        </svg>
      </div>

      <div
        ref={tooltipRef}
        className="fixed rounded-2xl"
        style={{
          ...tooltipStyle,
          left: tooltipLeft,
          width: tooltipMaxWidth,
          padding: '16px 18px',
          zIndex: 9999,
          pointerEvents: 'auto',
          background: '#ffffff',
          boxShadow: '0 14px 44px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.05)',
          border: '1px solid rgba(0, 0, 0, 0.06)',
        }}
        data-testid="coach-mark-tooltip"
      >
        {hasStepLabel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <div
              style={{
                flexShrink: 0,
                width: 7,
                height: 7,
                borderRadius: 999,
                background: tokens.accent,
                boxShadow: `0 0 0 3px ${tokens.accent}22`,
                animation: 'worthly-tour-pulse 1.6s ease-in-out infinite',
              }}
            />
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.12em',
                color: tokens.accentDark,
                textTransform: 'uppercase',
                lineHeight: 1,
                fontFamily: tourFont(),
              }}
            >
              Step {stepIndex + 1} of {stepTotal}
            </div>
          </div>
        )}

        <div
          style={{
            fontFamily: tourFont(),
            fontWeight: 600,
            fontSize: 17,
            lineHeight: 1.25,
            color: '#1a1a1a',
            marginBottom: body ? 4 : 0,
          }}
        >
          {title}
        </div>

        {body && (
          <div
            style={{
              fontSize: 13,
              color: '#6b7280',
              lineHeight: 1.45,
              fontFamily: tourFont(),
            }}
          >
            {body}
          </div>
        )}

        {skipLink}

        <style>{`
          @keyframes worthly-tour-pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.2); opacity: 0.85; }
          }
        `}</style>
      </div>
    </>
  );
};

export default CoachMark;
